// Technical notes. Status is either 'published' (with url/doi/date) or 'planned' (with month).
// Never mark a note as published without a live URL and a DOI.

export type Note = {
  slug: string;
  number?: number; // series number once published
  title: string;
  summary: string;
  status: 'published' | 'planned';
  date?: string; // ISO date when published
  month?: string; // e.g. 'October 2026' when planned
  url?: string; // canonical page: https://cardinalgrid.com/notes/<slug> once the plain-language page exists
  doi?: string; // e.g. 10.5281/zenodo.xxxxxxx
  code?: string; // repository path
  pdf?: string; // direct link to the archived PDF
};

export const NOTES: Note[] = [
  {
    slug: 'ba-forecast-scorecard-2015-2026',
    title: 'How well do U.S. balancing authorities forecast their own load? A 2015–2026 scorecard from EIA-930',
    summary:
      'EIA-930 publishes each balancing authority’s day-ahead demand forecast next to the demand that actually occurred. This note scores that forecast, BA by BA, over a decade.',
    status: 'published',
    number: 1,
    date: '2026-09-10',
    url: 'https://cardinalgrid.com/notes/ba-forecast-scorecard-2015-2026',
    doi: '10.5281/zenodo.22883589',
    code: 'https://github.com/cardinalgrid/notes/tree/main/01-ba-forecast-scorecard-2015-2026',
    pdf: 'https://zenodo.org/records/22883589/files/cardinal-grid-note-01-ba-forecast-scorecard-2015-2026.pdf',
  },
  {
    slug: 'daily-load-profiles-weekday-day-type-weather',
    title: 'Seven daily profiles, three, or none? What the load shape of 44 U.S. balancing authorities says about weekdays, holidays and weather',
    summary:
      'Three day-type profiles beat seven weekday profiles everywhere; recency beats the calendar; the analog days of the previous year help; a temperature regime from one NOAA station per BA is worth more than every calendar distinction combined; and only some holidays are special.',
    status: 'published',
    number: 2,
    date: '2026-09-13',
    url: 'https://cardinalgrid.com/notes/daily-load-profiles-weekday-day-type-weather',
    doi: '10.5281/zenodo.22735699',
    code: 'https://github.com/cardinalgrid/notes/tree/main/03-daily-load-profiles',
    pdf: 'https://zenodo.org/records/22735699/files/cardinal-grid-note-02-daily-load-profiles.pdf',
  },
  {
    slug: 'open-forecaster-43-bas',
    title: 'An open day-ahead load forecaster on 43 U.S. balancing authorities: ahead of the published forecasts, not of what they could be',
    summary:
      'A forecaster built only from public data, run unchanged on 43 BAs over 2023 to 2025, against each BA’s own day-ahead forecast. Most of its margin turns out to be a definition gap in the published forecasts; at the peak of the extreme days the operators are ahead.',
    status: 'published',
    number: 3,
    date: '2026-10-12',
    url: 'https://cardinalgrid.com/notes/open-forecaster-43-bas',
    doi: '10.5281/zenodo.23327590',
    code: 'https://github.com/cardinalgrid/notes/tree/main/05-open-forecaster-43-bas',
    pdf: 'https://zenodo.org/records/23327590/files/cardinal-grid-note-03-open-forecaster-43-bas.pdf',
  },
  {
    slug: 'prediction-intervals-winter-storms',
    title: 'Prediction intervals for day-ahead load that hold in winter storms: the weather blend’s spread as the width of the band',
    summary:
      'A 90 % band that keeps its promise over a year fails on storm days. Stretching it by how much the National Blend of Models’ members disagree restores the coverage at a small cost; what no band repairs is a forecast that sits low.',
    status: 'published',
    number: 4,
    date: '2026-10-12',
    url: 'https://cardinalgrid.com/notes/prediction-intervals-winter-storms',
    doi: '10.5281/zenodo.23327592',
    code: 'https://github.com/cardinalgrid/notes/tree/main/04-cold-regime-intervals',
    pdf: 'https://zenodo.org/records/23327592/files/cardinal-grid-note-04-prediction-intervals-winter-storms.pdf',
  },
  {
    slug: 'first-cold-morning-anomaly-detectors',
    title: 'Would a statistical anomaly detector have thrown away the first cold morning? A test on public telemetry from the January 2024 Arctic storms',
    summary:
      'FERC and NERC record the largest load under-forecasts early in winter storms, as the weather worsens. This note tests, on public data, whether an anomaly detector that preserves genuine extremes keeps those readings.',
    status: 'planned',
    month: 'November 2026',
  },
  {
    slug: 'step-loads-data-centers',
    title: 'Step loads: detecting data-center-scale structural breaks in ERCOT and PJM zonal load',
    summary:
      'Large loads arrive as steps, not trends. A structural-break approach to telling them apart from noise and weather.',
    status: 'planned',
    month: 'November 2026',
  },
  {
    slug: 'forecast-error-to-reserve-cost',
    title: 'From forecast error to reserve cost: a transparent back-of-envelope for ratepayers',
    summary:
      'What one percentage point of day-ahead error means in megawatts of reserve, and roughly what that costs.',
    status: 'planned',
    month: 'December 2026',
  },
  {
    slug: 'eia-930-imputation',
    title: 'How much of EIA-930 is imputed, and where? Data quality across 65 balancing authorities',
    summary:
      'Missing hours, imputed values and implausible jumps in the public hourly dataset, by BA and by year.',
    status: 'planned',
    month: 'December 2026',
  },
  {
    slug: 'state-of-us-load-forecasting-2026',
    title: 'State of U.S. load forecasting 2026: year-end scorecard and what the winter 2026–27 events showed',
    summary: 'The year in day-ahead forecast performance, with the winter events as they happened.',
    status: 'planned',
    month: 'January 2027',
  },
];
